import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  ApolloError,
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery
} from '@apollo/client';
import { setError, setLoading } from '@common/slices';
import { extractMessage } from '@utils/helper-utils';

export function useApiQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  key: string,
  options?: QueryHookOptions<TData, TVariables>,
  config?: {
    successMsg?: string;
    withLoader?: boolean;
    suppressErrorToast?: boolean;
  }
): QueryResult<TData, TVariables> {
  const dispatch = useDispatch();
  const withLoader = config?.withLoader ?? true; // default true
  const suppressErrorToast = config?.suppressErrorToast ?? false;
  const abortControllerRef = useRef<AbortController | null>(null);
  const location = useLocation();

  // Only create context once
  const context = useRef({
    fetchOptions: {
      signal: undefined as AbortSignal | undefined
    }
  }).current;

  // Set the signal if the request is active
  if (!context.fetchOptions.signal) {
    abortControllerRef.current = new AbortController();
    context.fetchOptions.signal = abortControllerRef.current.signal;
  }

  const result = useQuery<TData, TVariables>(query, {
    ...options,
    context,
    onCompleted: (data: TData) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (config?.successMsg) {
        toast.success(config?.successMsg, { autoClose: 3000 });
      }
      if (options?.onCompleted) options.onCompleted(data);
    },
    onError: (error: ApolloError) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (!error.message.includes('aborted')) {
        const cleanMessage = extractMessage(error.message);
        if (options?.onError) options.onError(error);
        dispatch(setError({ key, error: cleanMessage }));
        if (!suppressErrorToast) {
          toast.error(cleanMessage, { autoClose: 3000 });
        }
      }
    }
  });

  useEffect(() => {
    if (result.loading) {
      if (withLoader) {
        dispatch(setLoading({ key, value: true }));
      }
      dispatch(setError({ key, error: null }));
    }
  }, [result.loading]);

  // Clean up on unmount only
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return result;
}
